// ABOUTME: View switcher dropdown for ShDataGrid3 — replaces the static title when saved configs exist.
// ABOUTME: Lists saved views with delete. Clicking a view applies it via useGridLayout.actions.setActiveConfig.

import { memo, useCallback, useState } from "react";
import { Box, IconButton, useTheme } from "@mui/material";
import PopoverModal from "../Modal/PopoverModal";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useTranslation } from "react-i18next";

import { tokens } from "../../../theme";
import Subheading from "../text/Subheading";
import Text from "../text/Text";
import { DEFAULT_ACTIVE_CONFIG_ID } from "../../../newapi/global/userLayout/userLayoutSlice";

/**
 * ShGridConfigDropdown
 *
 * Sits in the ShDataGrid3 title bar. When no saved configs exist, it renders a
 * plain `<Subheading>` with the grid's `title` prop — identical to the pre-config
 * behavior. When one or more configs are saved, the heading becomes a clickable
 * dropdown trigger that opens a popover listing:
 *
 *   - "Default" (reset to code-column defaults, no saved config)
 *   - Each saved config with a delete inline affordance
 *   - Active config has a checkmark
 *
 * Clicking a row switches to that view via `onSelectConfig`. Save/create-new
 * functionality lives in a separate MSaveGridConfig dialog, not in this dropdown.
 *
 * Props:
 *   title          — the grid's static title (fallback label when activeConfigId is "default")
 *   explainer      — optional ReactNode appended after the title (e.g. a tooltip icon)
 *   configs        — array of saved config objects from useGridLayout
 *   activeConfigId — the currently active config id (or "default")
 *   onSelectConfig — (configId) => void, switches to a config (or "default" to reset)
 *   onDeleteConfig — (configId) => void (called for locally-owned configs)
 *   onDetachConfig — (configId) => void (called for shared configs — "unmount"/stop using)
 */
const ShGridConfigDropdown = memo(function ShGridConfigDropdown({
    title,
    explainer,
    configs,
    activeConfigId,
    defaultConfigId,
    onSelectConfig,
    onDeleteConfig,
    onDetachConfig,
    onSetDefaultConfig,
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const hasConfigs = Array.isArray(configs) && configs.length > 0;
    const isDefault = activeConfigId === DEFAULT_ACTIVE_CONFIG_ID;

    const activeLabel = isDefault
        ? title
        : configs?.find((c) => c.id === activeConfigId)?.name ?? title;

    const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), []);
    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleSelect = useCallback(
        (configId) => {
            if (onSelectConfig) onSelectConfig(configId);
            handleClose();
        },
        [onSelectConfig, handleClose]
    );

    // No saved configs — render the static title.
    if (!hasConfigs) {
        if (title === undefined || title === "") return null;
        return (
            <Subheading>
                {title}
                {explainer ?? null}
            </Subheading>
        );
    }

    const checkmark = (
        <CheckIcon
            fontSize="small"
            sx={{
                color:
                    theme.palette.mode === "dark"
                        ? colors.blue[400]
                        : colors.primary[400],
            }}
        />
    );


    return (
        <>
            <Box
                onClick={handleOpen}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    gap: 0.5,
                    userSelect: "none",
                    "&:hover": { opacity: 0.8 },
                }}
            >
                <Subheading>
                    {activeLabel}
                    {explainer ?? null}
                </Subheading>
                <KeyboardArrowDownIcon
                    fontSize="small"
                    sx={{ color: colors.txt.secondary }}
                />
            </Box>

            <PopoverModal
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                triangle={{ position: "center", color: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0] }}
                sx={{
                    body: {
                        minWidth: 260,
                        maxHeight: 400,
                        overflow: "auto",
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        borderRadius: 3,
                    },
                }}
            >
                <Box sx={{ py: 0.5 }}>
                    {/* Default (code-column defaults) */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        px: 2,
                        height: 40,
                        cursor: "pointer",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 14,
                        color: colors.txt.primary,
                        "&:hover": {
                            background:
                                theme.palette.mode === "dark" ? colors.grey[400] : colors.bg.tertiary,
                        },
                    }}>
                        <Box
                            onClick={() => handleSelect(DEFAULT_ACTIVE_CONFIG_ID)}
                            sx={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {isDefault ?
                                <CheckIcon
                                    fontSize="small"
                                    sx={{
                                        fontSize: 20,
                                        flexShrink: 0,
                                        color:
                                            theme.palette.mode === "dark"
                                                ? colors.blue[400]
                                                : colors.primary[400],
                                    }}
                                />
                                :
                                <Box sx={{ width: 20, height: 20, flexShrink: 0, visibility: "hidden" }} />
                            }
                            <Text semibold>{title}</Text>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                            
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onSetDefaultConfig) onSetDefaultConfig(DEFAULT_ACTIVE_CONFIG_ID);
                                }}
                                sx={{ width: 28, height: 28 }}
                                title={t("set_as_default") ?? "Set as default"}
                            >
                                {defaultConfigId === DEFAULT_ACTIVE_CONFIG_ID ? (
                                    <StarIcon
                                        fontSize="small"
                                        sx={{
                                            fontSize: 16,
                                            color:
                                                theme.palette.mode === "dark"
                                                    ? colors.blue[400]
                                                    : colors.primary[400],
                                        }}
                                    />
                                ) : (
                                    <StarBorderIcon
                                        fontSize="small"
                                        sx={{ fontSize: 16, color: colors.txt.secondary }}
                                    />
                                )}
                            </IconButton>
                            <Box sx={{ width: 28, height: 28, visibility: "hidden" }} />
                        </Box>
                    </Box>

                    {/* Saved views */}
                    {configs.map((config) => {
                        const isActive = activeConfigId === config.id;
                        return (
                            <Box key={config.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    px: 2,
                                    height: 40,
                                    cursor: "pointer",
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: 14,
                                    color: colors.txt.primary,
                                    "&:hover": {
                                        background:
                                            theme.palette.mode === "dark" ? colors.grey[400] : colors.bg.tertiary,
                                    },
                                }}
                            >
                                <Box
                                    onClick={() => handleSelect(config.id)}
                                    sx={{
                                        flex: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {isActive ?
                                        <CheckIcon
                                            fontSize="small"
                                            sx={{
                                                fontSize: 20,
                                                flexShrink: 0,
                                                color:
                                                    theme.palette.mode === "dark"
                                                        ? colors.blue[400]
                                                        : colors.primary[400],
                                            }}
                                        />
                                        :
                                        <Box sx={{ width: 20, height: 20, flexShrink: 0, visibility: "hidden" }} />
                                    }
                                    <Text medium>{config.name}</Text>
                                    {config.isShared && (
                                        <LinkIcon
                                            fontSize="small"
                                            titleAccess={t("shared_view") ?? "Shared view"}
                                            sx={{
                                                fontSize: 14,
                                                ml: 0.5,
                                                color: colors.txt.secondary,
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        flexShrink: 0,
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onSetDefaultConfig) onSetDefaultConfig(config.id);
                                        }}
                                        sx={{ width: 28, height: 28 }}
                                        title={t("set_as_default") ?? "Set as default"}
                                    >
                                        {defaultConfigId === config.id ? (
                                            <StarIcon
                                                fontSize="small"
                                                sx={{
                                                    fontSize: 16,
                                                    color:
                                                        theme.palette.mode === "dark"
                                                            ? colors.blue[400]
                                                            : colors.primary[400],
                                                }}
                                            />
                                        ) : (
                                            <StarBorderIcon
                                                fontSize="small"
                                                sx={{ fontSize: 16, color: colors.txt.secondary }}
                                            />
                                        )}
                                    </IconButton>
                                    {config.isShared ? (
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onDetachConfig) onDetachConfig(config.id);
                                            }}
                                            sx={{ width: 28, height: 28 }}
                                            title={t("stop_using_shared_view") ?? "Stop using this shared view"}
                                        >
                                            <LinkOffIcon
                                                fontSize="small"
                                                sx={{ fontSize: 16, color: colors.txt.secondary }}
                                            />
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onDeleteConfig) onDeleteConfig(config.id);
                                            }}
                                            sx={{ width: 28, height: 28 }}
                                        >
                                            <DeleteOutlineIcon
                                                fontSize="small"
                                                sx={{ fontSize: 16, color: colors.txt.secondary }}
                                            />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </PopoverModal>
        </>
    );
});

export default ShGridConfigDropdown;
